/**
 * Prove — production page-family (WP1c / #46599, WP6j–WP6l / #46634–#46636).
 *
 * Receipts live in pages/receipts. Packs and audit-room use the WP4d
 * verificationProve contracts for both local Demo and Product dogfood.
 */

import { useEffect, useMemo, useState } from "react";
import { formatBytes, formatDateTime, formatNumber, formatRelativeTime } from "@excellent/ui";
import { ApiError, apiCall, useApi } from "@excellent/ui/api";
import type {
  VerificationAccessLogDto,
  VerificationAuditGrantDto,
  VerificationPackDto,
} from "@excellent/ui/api/contracts/verification-prove";
import {
  ReadOnlyFailureCallout,
  readOnlyFailureNoticeFromError,
  type ActionNotice,
} from "../read-only-errors";
import type { VerificationEnvironment } from "../env";
import { href } from "../nav";
import { useVerificationEnvOptional } from "../env-provider";
import {
  ActionButton,
  ButtonRow,
  Callout,
  Card,
  CardContent,
  DataTable,
  Field,
  Fields,
  FilterField,
  FilterToolbar,
  ListSearch,
  Mono,
  PageActions,
  Stat,
  StatGrid,
  ToneBadge,
  type DataTableColumn,
} from "../ui";
import {
  PageError,
  PageFrame,
  PageLink,
  PagePending,
  type VerificationDetailPageProps,
  type VerificationPageProps,
} from "./page-frame";
import { verificationCanonicalClients } from "../api/canonical-clients";

export { ReceiptDetailPage, ReceiptsPage } from "./receipts";

export const RECEIPTS_OPEN_DETAIL_ACTION = "receipts.open-detail";
export const RECEIPTS_REPLAY_ACTION = "receipts.replay";
export const PACKS_COMPOSE_ACTION = "packs.compose";
export const PACKS_DOWNLOAD_ACTION = "packs.download";
export const AUDIT_ROOM_INVITE_ACTION = "audit-room.invite";
export const AUDIT_ROOM_REVOKE_ACTION = "audit-room.revoke";

const PACKS_LEDE =
  "A scoped export of run, control, and receipt identifiers with limitation metadata. Packs do not contain signed receipt envelopes, evidence bodies, or independently obtained trust material.";
const PACK_DETAIL_LEDE =
  "One scoped export of identifiers and limitation metadata: the runs, controls and receipts it names, and what it does not carry.";
const AUDIT_ROOM_LEDE =
  "Time-limited, read-only access for people outside your organisation. They see scoped packs and nothing else. Grants, revocations, downloads, redactions and expiries are recorded; those rows do not prove that a reviewer accessed a pack.";

// Held as constants, not inline JSX text, so the formatter cannot re-wrap these
// sentences and silently break the phrasing the contract gate pins.
const PACK_SCOPE_NOTE =
  "Artifact identifiers and the associated artifact count shown above are not included in the download.";

const PACK_HASH_NOTE =
  "The bundle hash covers the environment, the pack identifier and the run identifiers; it supports comparison with an independently retained expected hash, and does not prove origin or resist deliberate recomputation.";

const REVIEWERS: Record<VerificationEnvironment, { id: string; name: string; email: string }[]> = {
  demo: [
    { id: "demo:act_ext", name: "External Auditor", email: "auditor@example.com" },
    { id: "demo:act_auditor_backup", name: "Backup Reviewer", email: "backup-reviewer@example.com" },
  ],
  real: [
    { id: "real:act_auditor", name: "Customer Auditor", email: "auditor@excellent.so" },
    { id: "real:act_customer_reviewer", name: "Customer Reviewer", email: "reviewer@customer.example" },
  ],
};

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "The action did not complete.";
}

function useLastFetchedAt(data: unknown): string | null {
  const [lastFetchedAt, setLastFetchedAt] = useState<string | null>(null);
  useEffect(() => {
    if (data) setLastFetchedAt(new Date().toISOString());
  }, [data]);
  return lastFetchedAt;
}

function LastFetched({ at, stale }: { at: string | null; stale?: boolean }) {
  if (!at) return null;
  return (
    <Callout tone={stale ? "unknown" : "neutral"} title={stale ? "Showing stale data" : "Real data loaded"}>
      Last fetched {formatDateTime(at)}.
    </Callout>
  );
}

function environmentActor(environment: VerificationEnvironment): string {
  return environment === "demo" ? "demo:act_mw" : "real:act_matt";
}

function triggerPackDownload(filename: string, body: string) {
  if (typeof document === "undefined") return;
  const blob = new Blob([body], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function packKindLabel(kind: VerificationPackDto["kind"]): string {
  return kind.replace(/_/g, " ");
}

function packColumns(
  onNavigate: (to: string) => void,
  onDownload: (pack: VerificationPackDto) => void,
): DataTableColumn<VerificationPackDto>[] {
  return [
    {
      key: "pack",
      header: "Pack",
      cellClassName: "whitespace-normal",
      render: (pack) => (
        <div
          className="flex flex-col gap-0.5"
          data-pack-id={pack.id}
          data-demo-scoped={pack.provenance.demoScoped ? "true" : undefined}
        >
          <span className="font-medium text-foreground">{pack.label}</span>
          <Mono>{pack.id}</Mono>
        </div>
      ),
    },
    {
      key: "controls",
      header: "Controls",
      render: (pack) => <Mono>{formatNumber(pack.controlIds.length)}</Mono>,
    },
    {
      key: "receipts",
      header: "Receipts",
      render: (pack) => <Mono>{formatNumber(pack.receiptIds.length)}</Mono>,
    },
    {
      key: "limitations",
      header: "Limitations",
      render: (pack) =>
        pack.scopeLimitations.length === 0 ? (
          <span className="text-muted-foreground">None</span>
        ) : (
          <ToneBadge tone="unknown">{formatNumber(pack.scopeLimitations.length)}</ToneBadge>
        ),
    },
    {
      key: "created",
      header: "Created",
      render: (pack) => <Mono>{formatRelativeTime(pack.createdAt)}</Mono>,
    },
    {
      key: "actions",
      header: "Actions",
      render: (pack) => (
        <ButtonRow>
          <ActionButton
            actionId={PACKS_DOWNLOAD_ACTION}
            data-action-id={PACKS_DOWNLOAD_ACTION}
            data-pack-id={pack.id}
            onClick={(event) => {
              event.stopPropagation();
              onDownload(pack);
            }}
          >
            Download
          </ActionButton>
          <ActionButton
            actionId="packs.open-row"
            data-action-id="packs.open-row"
            variant="primary"
            data-pack-id={pack.id}
            onClick={(event) => {
              event.stopPropagation();
              onNavigate(href("packs", pack.id));
            }}
          >
            Open pack
          </ActionButton>
          {pack.downloadedAt ? (
            <ToneBadge tone="accent">{`downloaded ${formatRelativeTime(pack.downloadedAt)}`}</ToneBadge>
          ) : null}
        </ButtonRow>
      ),
    },
  ];
}

export function PacksPage({ onNavigate }: VerificationPageProps) {
  const env = useVerificationEnvOptional();
  const environment: VerificationEnvironment | null = env?.environment ?? null;
  const ready = env?.ready ?? true;
  const [q, setQ] = useState("");
  const [notice, setNotice] = useState<ActionNotice | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  const packsApi = useApi(
    verificationCanonicalClients.packsList.operation,
    environment && ready ? { query: { env: environment } } : null,
  );
  const runsApi = useApi(
    verificationCanonicalClients.runsList.operation,
    environment && ready ? { query: { env: environment } } : null,
  );
  const lastFetchedAt = useLastFetchedAt(packsApi.data);

  const catalog = useMemo(() => {
    return [...(packsApi.data?.items ?? [])].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [packsApi.data]);
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return catalog;
    return catalog.filter(
      (pack) =>
        pack.id.toLowerCase().includes(needle) ||
        pack.label.toLowerCase().includes(needle) ||
        pack.kind.toLowerCase().includes(needle),
    );
  }, [catalog, q]);
  const completedRunIds = useMemo(
    () => (runsApi.data?.items ?? []).filter((run) => run.lifecycle === "completed").map((run) => run.id),
    [runsApi.data],
  );
  const composeDisabledReason =
    !environment
      ? "The active Environment must load before composing a pack."
      : completedRunIds.length === 0
        ? "No completed run is available to include in a pack."
        : pending === PACKS_COMPOSE_ACTION
          ? "Composing pack."
          : null;

  async function onCompose() {
    if (!environment || composeDisabledReason) return;
    setPending(PACKS_COMPOSE_ACTION);
    setNotice(null);
    try {
      const result = await apiCall(verificationCanonicalClients.packsCompose.operation, {
        query: { env: environment },
        body: {
          actorId: environmentActor(environment),
          label: `${environment === "demo" ? "Demo" : "Dogfood"} evidence pack ${new Date().toISOString()}`,
          kind: "offline_replay",
          runIds: completedRunIds,
        },
      });
      await packsApi.mutate();
      setNotice(`Composed ${result.pack.label}.`);
    } catch (err) {
      setNotice(readOnlyFailureNoticeFromError(err) ?? errorMessage(err));
    } finally {
      setPending(null);
    }
  }

  async function onDownload(pack: VerificationPackDto) {
    if (!environment) return;
    setPending(pack.id);
    setNotice(null);
    try {
      const result = await apiCall(verificationCanonicalClients.packsDownload.operation, {
        params: { id: pack.id },
        query: { env: environment },
        body: { actorId: environmentActor(environment) },
      });
      triggerPackDownload(result.filename, result.body);
      await packsApi.mutate();
      setNotice(`Downloaded ${result.filename}.`);
    } catch (err) {
      setNotice(readOnlyFailureNoticeFromError(err) ?? errorMessage(err));
    } finally {
      setPending(null);
    }
  }

  if (env && !ready) {
    return (
      <PageFrame page="packs" wide eyebrow="Prove" title="Evidence packs" lede={PACKS_LEDE}>
        <PagePending title="Loading environment">Loading packs for the active Environment.</PagePending>
      </PageFrame>
    );
  }

  if (!environment) {
    return (
      <PageFrame page="packs" wide eyebrow="Prove" title="Evidence packs" lede={PACKS_LEDE}>
        <PagePending title="Environment unavailable">Packs are scoped to the active Environment.</PagePending>
      </PageFrame>
    );
  }

  return (
    <PageFrame
      page="packs"
      wide
      eyebrow="Prove"
      title="Evidence packs"
      lede={PACKS_LEDE}
      actions={
        <PageActions
          primary={
            <ActionButton
              actionId={PACKS_COMPOSE_ACTION}
              data-action-id={PACKS_COMPOSE_ACTION}
              variant="primary"
              disabled={Boolean(composeDisabledReason)}
              reason={composeDisabledReason ?? undefined}
              onClick={onCompose}
            >
              Compose a pack
            </ActionButton>
          }
        />
      }
    >
  <LastFetched at={lastFetchedAt} stale={Boolean(packsApi.error && packsApi.data)} />
  {packsApi.error ? (
    <PageError title="Packs failed to load" error={packsApi.error} onRetry={() => void packsApi.mutate()} />
  ) : null}
  {runsApi.error ? (
    <PageError title="Runs failed to load" error={runsApi.error} onRetry={() => void runsApi.mutate()} />
  ) : null}
  {notice ? <ReadOnlyFailureCallout notice={notice} title="Action result" actionId="packs.action-status" /> : null}
      {packsApi.error ? <Callout tone="fail" title="Packs did not load">{errorMessage(packsApi.error)}</Callout> : null}
      <DataTable
        toolbar={
          <FilterToolbar>
            <FilterField label="Search" wide>
              <ListSearch
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search packs..."
                aria-label="Search packs"
              />
            </FilterField>
          </FilterToolbar>
        }
        emptyState={{
          title: packsApi.isLoading ? "Loading packs" : catalog.length === 0 ? "No packs yet" : "No packs match",
          description:
            catalog.length === 0 ? (
              <>
                A pack that implies coverage the evidence does not support is refused, not exported.{" "}
                <PageLink onNavigate={onNavigate} to="audit-room">
                  Open the audit room
                </PageLink>
                .
              </>
            ) : (
              "Clear search to see every composed pack."
            ),
        }}
        rows={rows}
        rowKey={(pack) => pack.id}
        rowActionId="packs.open-row"
        onRowClick={(pack) => onNavigate(href("packs", pack.id))}
        columns={packColumns(onNavigate, onDownload)}
      />
      {pending && pending !== PACKS_COMPOSE_ACTION ? <p className="type-body text-muted-foreground">Preparing download...</p> : null}
    </PageFrame>
  );
}

export function PackDetailPage({ id, onNavigate }: VerificationDetailPageProps) {
  const env = useVerificationEnvOptional();
  const environment: VerificationEnvironment | null = env?.environment ?? null;
  const ready = env?.ready ?? true;
  const [notice, setNotice] = useState<ActionNotice | null>(null);
  const [pending, setPending] = useState(false);
  const packApi = useApi(
    verificationCanonicalClients.packsGet.operation,
    environment && ready ? { params: { id }, query: { env: environment } } : null,
  );
  const lastFetchedAt = useLastFetchedAt(packApi.data);
  const pack = packApi.data?.pack ?? null;

  async function onDownload() {
    if (!environment || !pack) return;
    setPending(true);
    setNotice(null);
    try {
      const result = await apiCall(verificationCanonicalClients.packsDownload.operation, {
        params: { id: pack.id },
        query: { env: environment },
        body: { actorId: environmentActor(environment) },
      });
      triggerPackDownload(result.filename, result.body);
      await packApi.mutate();
      setNotice(`Downloaded ${result.filename}.`);
    } catch (err) {
      setNotice(readOnlyFailureNoticeFromError(err) ?? errorMessage(err));
    } finally {
      setPending(false);
    }
  }

  if (env && !ready) {
    return (
      <PageFrame page="pack" wide eyebrow="Prove" title="Evidence pack" lede={PACK_DETAIL_LEDE}>
        <PagePending title="Loading environment">Loading this pack for the active Environment.</PagePending>
      </PageFrame>
    );
  }

  if (!environment) {
    return (
      <PageFrame page="pack" wide eyebrow="Prove" title="Evidence pack" lede={PACK_DETAIL_LEDE}>
        <PagePending title={`No pack loaded for ${id}`}>
          Load the active Environment to open this pack.{" "}
          <PageLink onNavigate={onNavigate} to="packs">
            Back to evidence packs
          </PageLink>
          .
        </PagePending>
      </PageFrame>
    );
  }

  if (packApi.error && !packApi.data) {
    return (
      <PageFrame page="pack" wide eyebrow="Prove" title="Evidence pack" lede={PACK_DETAIL_LEDE}>
        <PageError title={`No pack loaded for ${id}`} error={packApi.error} onRetry={() => void packApi.mutate()} />
      </PageFrame>
    );
  }

  if (packApi.isLoading || !pack) {
    return (
      <PageFrame page="pack" wide eyebrow="Prove" title="Evidence pack" lede={PACK_DETAIL_LEDE}>
        <PagePending title={packApi.isLoading ? `Loading ${id}` : `No pack loaded for ${id}`}>
          Reading the pack from verificationCanonicalClients.packsGet.operation.{" "}
          <PageLink onNavigate={onNavigate} to="packs">
            Back to evidence packs
          </PageLink>
          .
        </PagePending>
      </PageFrame>
    );
  }

  return (
    <PageFrame
      page="pack"
      wide
      eyebrow={`Evidence pack · ${packKindLabel(pack.kind)}`}
      title={pack.label}
      lede={`Composed ${formatDateTime(pack.createdAt)}. ${pack.expiresAt ? `Access expires ${formatDateTime(pack.expiresAt)}.` : "No expiry."}`}
      actions={
        <ActionButton
          actionId={PACKS_DOWNLOAD_ACTION}
          data-action-id={PACKS_DOWNLOAD_ACTION}
          variant="primary"
          data-pack-id={pack.id}
          disabled={pending}
          reason={pending ? "Preparing download." : undefined}
          onClick={onDownload}
        >
          Download
        </ActionButton>
      }
    >
      <LastFetched at={lastFetchedAt} stale={Boolean(packApi.error && packApi.data)} />
      {notice ? <ReadOnlyFailureCallout notice={notice} title="Action result" actionId="pack.action-status" /> : null}
      <ButtonRow>
        <ToneBadge tone={pack.offlineVerified ? "pass" : "unknown"}>
          {pack.offlineVerified ? "replays offline" : "replay unchecked"}
        </ToneBadge>
        <ToneBadge tone="neutral">{formatBytes(pack.sizeBytes)}</ToneBadge>
        {pack.downloadedAt ? (
          <ToneBadge tone="accent">{`downloaded ${formatRelativeTime(pack.downloadedAt)}`}</ToneBadge>
        ) : null}
      </ButtonRow>

      <Card>
        <CardContent>
          <Fields>
            <Field label="Bundle hash">
              <Mono>{pack.bundleHash}</Mono>
            </Field>
            <Field label="Controls">
              <Mono>{formatNumber(pack.controlIds.length)}</Mono>
            </Field>
            <Field label="Receipts">
              <Mono>{formatNumber(pack.receiptIds.length)}</Mono>
            </Field>
            <Field label="Associated artifact count">
              <Mono>{formatNumber(pack.artifactCount)}</Mono>
            </Field>
          </Fields>
        </CardContent>
      </Card>

      <Callout tone="unknown" title="What the bundle hash covers">
        {PACK_SCOPE_NOTE} {PACK_HASH_NOTE}
      </Callout>

      {pack.scopeLimitations.length > 0 ? (
        <Callout tone="unknown" title="What this pack does not claim">
          {pack.scopeLimitations.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </Callout>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <ActionButton actionId="nav.packs" onClick={() => onNavigate(href("packs"))}>
          Back to evidence packs
        </ActionButton>
      </div>
    </PageFrame>
  );
}

type AuditGrantRow = {
  grant: VerificationAuditGrantDto;
  pack?: VerificationPackDto;
  reviewer: { id: string; name: string; email: string };
};

function reviewerFor(environment: VerificationEnvironment, actorId: string): { id: string; name: string; email: string } {
  return (
    REVIEWERS[environment].find((reviewer) => reviewer.id === actorId) ?? {
      id: actorId,
      name: actorId,
      email: actorId,
    }
  );
}

function auditGrantColumns(
  revoke: (grant: VerificationAuditGrantDto) => void,
): DataTableColumn<AuditGrantRow>[] {
  return [
    {
      key: "reviewer",
      header: "Reviewer",
      cellClassName: "whitespace-normal",
      render: (row) => (
        <div className="flex flex-col gap-0.5" data-demo-scoped={row.pack?.provenance.demoScoped ? "true" : undefined} data-actor-id={row.reviewer.id}>
          <span className="font-medium text-foreground">{row.reviewer.name}</span>
          <span className="text-muted-foreground">{row.reviewer.email}</span>
        </div>
      ),
    },
    {
      key: "pack",
      header: "Pack",
      cellClassName: "whitespace-normal",
      render: (row) => (
        <div className="flex flex-col gap-0.5">
          <span className="font-medium text-foreground">{row.pack?.label ?? row.grant.packId}</span>
          <span className="text-muted-foreground">
            {formatNumber(row.grant.views)} views · {formatNumber(row.grant.downloads)} downloads
          </span>
        </div>
      ),
    },
    {
      key: "expires",
      header: "Expires",
      render: (row) => formatDateTime(row.grant.expiresAt),
    },
    {
      key: "state",
      header: "State",
      render: (row) =>
        row.grant.revokedAt ? (
          <ToneBadge tone="fail">{`revoked ${formatRelativeTime(row.grant.revokedAt)}`}</ToneBadge>
        ) : (
          <ToneBadge tone="pass">active</ToneBadge>
        ),
    },
    {
      key: "action",
      header: "Action",
      render: (row) => (
        <ButtonRow>
          <ActionButton
            actionId={AUDIT_ROOM_REVOKE_ACTION}
            data-action-id={AUDIT_ROOM_REVOKE_ACTION}
            variant="reject"
            data-grant-id={row.grant.id}
            disabled={Boolean(row.grant.revokedAt)}
            reason={row.grant.revokedAt ? "This grant is already revoked." : undefined}
            onClick={() => revoke(row.grant)}
          >
            Revoke access
          </ActionButton>
        </ButtonRow>
      ),
    },
  ];
}

function accessLogColumns(): DataTableColumn<VerificationAccessLogDto>[] {
  return [
    {
      key: "action",
      header: "Action",
      render: (log) => <ToneBadge tone="neutral">{log.action}</ToneBadge>,
    },
    {
      key: "actor",
      header: "Actor",
      render: (log) => <Mono>{log.actorId}</Mono>,
    },
    {
      key: "pack",
      header: "Pack",
      render: (log) => (log.packId ? <Mono>{log.packId}</Mono> : <span className="text-muted-foreground">—</span>),
    },
    {
      key: "occurred",
      header: "Occurred",
      render: (log) => formatDateTime(log.occurredAt),
    },
  ];
}

export function AuditRoomPage({ onNavigate }: VerificationPageProps) {
  const env = useVerificationEnvOptional();
  const environment: VerificationEnvironment | null = env?.environment ?? null;
  const ready = env?.ready ?? true;
  const [notice, setNotice] = useState<ActionNotice | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const auditApi = useApi(
    verificationCanonicalClients.auditRoomGet.operation,
    environment && ready ? { query: { env: environment } } : null,
  );
  const packsApi = useApi(
    verificationCanonicalClients.packsList.operation,
    environment && ready ? { query: { env: environment } } : null,
  );
  const lastFetchedAt = useLastFetchedAt(auditApi.data);

  const grants = auditApi.data?.grants ?? [];
  const accessLogs = auditApi.data?.accessLogs ?? [];
  const packs = packsApi.data?.items ?? [];
  const activeGrantCount = grants.filter((grant) => !grant.revokedAt).length;
  const views = grants.reduce((sum, grant) => sum + grant.views, 0);
  const grantRows = useMemo(() => {
    if (!environment) return [];
    return grants.map((grant) => ({
      grant,
      pack: packs.find((pack) => pack.id === grant.packId),
      reviewer: reviewerFor(environment, grant.reviewerActorId),
    }));
  }, [environment, grants, packs]);
  const inviteCandidate = useMemo(() => {
    if (!environment) return null;
    for (const pack of [...packs].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))) {
      const reviewer = REVIEWERS[environment].find(
        (candidate) => !pack.sharedWith.some((share) => share.actorId === candidate.id),
      );
      if (reviewer) return { pack, reviewer };
    }
    return null;
  }, [environment, packs]);
  const inviteDisabledReason =
    !environment
      ? "The active Environment must load before inviting a reviewer."
      : packs.length === 0
        ? "No pack to share. Compose one first."
        : inviteCandidate
          ? pending === AUDIT_ROOM_INVITE_ACTION
            ? "Inviting reviewer."
            : null
          : "Every seeded reviewer already has access to every pack.";

  async function onInvite() {
    if (!environment || !inviteCandidate || inviteDisabledReason) return;
    setPending(AUDIT_ROOM_INVITE_ACTION);
    setNotice(null);
    try {
      const result = await apiCall(verificationCanonicalClients.auditRoomGrant.operation, {
        query: { env: environment },
        body: {
          actorId: environmentActor(environment),
          packId: inviteCandidate.pack.id,
          reviewerActorId: inviteCandidate.reviewer.id,
          expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        },
      });
      await Promise.all([auditApi.mutate(), packsApi.mutate()]);
      setNotice(`Invited ${reviewerFor(environment, result.grant.reviewerActorId).name}.`);
    } catch (err) {
      setNotice(readOnlyFailureNoticeFromError(err) ?? errorMessage(err));
    } finally {
      setPending(null);
    }
  }

  async function onRevoke(grant: VerificationAuditGrantDto) {
    if (!environment || grant.revokedAt) return;
    setPending(grant.id);
    setNotice(null);
    try {
      await apiCall(verificationCanonicalClients.auditRoomRevoke.operation, {
        params: { id: grant.id },
        query: { env: environment },
        body: { actorId: environmentActor(environment) },
      });
      await Promise.all([auditApi.mutate(), packsApi.mutate()]);
      setNotice(`Revoked ${reviewerFor(environment, grant.reviewerActorId).name}.`);
    } catch (err) {
      setNotice(readOnlyFailureNoticeFromError(err) ?? errorMessage(err));
    } finally {
      setPending(null);
    }
  }

  const inviteButton = (
    <ActionButton
      actionId={AUDIT_ROOM_INVITE_ACTION}
      data-action-id={AUDIT_ROOM_INVITE_ACTION}
      variant="primary"
      disabled={Boolean(inviteDisabledReason)}
      reason={inviteDisabledReason ?? undefined}
      onClick={onInvite}
    >
      Invite a reviewer
    </ActionButton>
  );

  if (env && !ready) {
    return (
      <PageFrame page="audit-room" eyebrow="Prove" title="Audit room" lede={AUDIT_ROOM_LEDE}>
        <PagePending title="Loading environment">Loading the audit room for the active Environment.</PagePending>
      </PageFrame>
    );
  }

  if (!environment) {
    return (
      <PageFrame page="audit-room" eyebrow="Prove" title="Audit room" lede={AUDIT_ROOM_LEDE}>
        <PagePending title="Environment unavailable">Audit-room grants are scoped to the active Environment.</PagePending>
      </PageFrame>
    );
  }

  return (
    <PageFrame page="audit-room" eyebrow="Prove" title="Audit room" lede={AUDIT_ROOM_LEDE} actions={inviteButton}>
      <LastFetched at={lastFetchedAt} stale={Boolean(auditApi.error && auditApi.data)} />
      {notice ? <ReadOnlyFailureCallout notice={notice} title="Action result" actionId="audit-room.action-status" /> : null}
      {auditApi.error ? (
        <PageError title="Audit room did not load" error={auditApi.error} onRetry={() => void auditApi.mutate()} />
      ) : null}
      <StatGrid cols={3}>
        <Stat label="Active grants" value={activeGrantCount} />
        <Stat label="Views recorded" value={views} foot="Recorded at compose; rows are not hash-chained" />
        <Stat label="Access logs" value={accessLogs.length} />
      </StatGrid>

      {grantRows.length === 0 ? (
        <PagePending title={auditApi.isLoading ? "Loading reviewers" : "No reviewers invited"}>
          External reviewers see the packs shared with them, not the workspace.{" "}
          <PageLink onNavigate={onNavigate} to="packs">
            Open evidence packs
          </PageLink>
          .
        </PagePending>
      ) : (
        <DataTable
          title="External reviewers"
          subtitle="What each one can see, and for how long."
          rows={grantRows}
          rowKey={(row) => row.grant.id}
          columns={auditGrantColumns(onRevoke)}
        />
      )}

      <DataTable
        title="Access log"
        subtitle="Grants, revocations, downloads, redactions, expiries and replays recorded for this environment."
        rows={[...accessLogs].sort((a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt)).slice(0, 12)}
        rowKey={(log) => log.id}
        columns={accessLogColumns()}
        emptyState={{
          title: pending ? "Writing access event" : "No access events",
          description: "Invite, revoke, replay, or download to append an audit-room event.",
        }}
      />
    </PageFrame>
  );
}
